import React, { useState } from "react";
import {
  Box, Button, HStack, Stack, Text, Tooltip, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from "@chakra-ui/react";
import { FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiInfo } from "react-icons/fi";
import { STATUS, useVizTokens } from "./tokens";

// The SMS Spend dashboard's building blocks. Text always wears the text colours; a coloured mark
// beside it carries the series. Every chart card can show its numbers as a table instead.

const STATUS_ICONS = {
  critical: FiAlertOctagon, serious: FiAlertTriangle, warning: FiAlertTriangle, good: FiCheckCircle, info: FiInfo,
};

export function StatusIcon({ status, size = 16 }) {
  const t = useVizTokens();
  const Icon = STATUS_ICONS[status] || FiInfo;
  return <Box as={Icon} boxSize={`${size}px`} flexShrink={0} color={STATUS[status] || t.muted} aria-hidden />;
}

export function DataTable({ columns, rows, caption }) {
  const t = useVizTokens();
  return (
    <TableContainer maxH="420px" overflowY="auto">
      <Table size="sm" variant="simple">
        {caption && <caption style={{ captionSide: "top", textAlign: "left", color: t.textSecondary }}>{caption}</caption>}
        <Thead position="sticky" top={0} bg={t.surface} zIndex={1}>
          <Tr>
            {columns.map((c) => (
              <Th key={c.key} isNumeric={c.numeric} color={t.textSecondary} borderColor={t.grid}>{c.label}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, i) => (
            <Tr key={row.key ?? row.id ?? i}>
              {columns.map((c) => (
                <Td key={c.key} isNumeric={c.numeric} color={t.text} borderColor={t.grid}
                    sx={c.numeric ? { fontVariantNumeric: "tabular-nums" } : undefined}
                    whiteSpace={c.wrap ? "normal" : "nowrap"} maxW={c.wrap ? "420px" : undefined}>
                  {c.render ? c.render(row) : row[c.key]}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

// A chart's frame: its title says what is plotted; `table` ({columns, rows}) is its table view.
export function ChartCard({ title, subtitle, table, actions, children, ...rest }) {
  const t = useVizTokens();
  const [asTable, setAsTable] = useState(false);
  return (
    <Box as="figure" m={0} bg={t.surface} borderWidth="1px" borderColor={t.border} borderRadius="lg" p={4} {...rest}>
      <HStack justify="space-between" align="flex-start" spacing={3} mb={3}>
        <Box minW={0}>
          <Text as="figcaption" fontWeight="semibold" color={t.text}>{title}</Text>
          {subtitle && <Text fontSize="sm" color={t.textSecondary}>{subtitle}</Text>}
        </Box>
        <HStack spacing={1} flexShrink={0}>
          {actions}
          {table && (
            <Button size="xs" variant="ghost" color={t.textSecondary} aria-pressed={asTable}
                    onClick={() => setAsTable((v) => !v)}>
              {asTable ? "Chart" : "Table"}
            </Button>
          )}
        </HStack>
      </HStack>
      {asTable && table ? <DataTable {...table} /> : children}
    </Box>
  );
}

// label · value · help, with a status (icon and words) when the number needs attention.
export function StatTile({ label, value, help, status, statusWords }) {
  const t = useVizTokens();
  return (
    <Box bg={t.surface} borderWidth="1px" borderColor={t.border} borderRadius="lg" p={4} minW={0}>
      <Text fontSize="xs" color={t.textSecondary}>{label}</Text>
      <Text fontSize="2xl" fontWeight="semibold" color={t.text} lineHeight="short" mt={1}>{value}</Text>
      {help && <Text fontSize="xs" color={t.muted} mt={1}>{help}</Text>}
      {status && (
        <HStack spacing={1.5} mt={2}>
          <StatusIcon status={status} size={14} />
          <Text fontSize="xs" color={t.textSecondary}>{statusWords}</Text>
        </HStack>
      )}
    </Box>
  );
}

// Horizontal bars from one baseline, one row each: {key, label, sub, value, valueLabel, color,
// tip}. One series takes one colour; `color` is for a row that must stand apart (gray for "not a
// person"). The value sits at the bar's tip; the row is the hover and focus target.
export function BarList({ rows, color, empty = "Nothing in this range.", barHeight = 12 }) {
  const t = useVizTokens();
  const max = Math.max(0, ...rows.map((r) => r.value || 0));
  if (!rows.length) return <Text fontSize="sm" color={t.muted}>{empty}</Text>;
  return (
    <Stack spacing={2.5}>
      {rows.map((row) => {
        const width = max > 0 ? Math.max(0.5, ((row.value || 0) / max) * 100) : 0;
        return (
          <Tooltip key={row.key} label={row.tip || `${row.label}: ${row.valueLabel}`} placement="top-start"
                   hasArrow openDelay={150}>
            <Box tabIndex={0} borderRadius="md" px={1} mx={-1} py={0.5} _hover={{ bg: t.hover }}
                 _focusVisible={{ outline: "2px solid", outlineColor: t.series[0] }}
                 aria-label={row.tip || `${row.label}: ${row.valueLabel}`}>
              <HStack justify="space-between" spacing={3} mb={1}>
                <Text fontSize="sm" color={t.text} noOfLines={1}>{row.label}</Text>
                {row.sub && <Text fontSize="xs" color={t.textSecondary} flexShrink={0}>{row.sub}</Text>}
              </HStack>
              <HStack spacing={2}>
                <Box flex="1" minW={0}>
                  <Box h={`${barHeight}px`} w={`${width}%`} bg={row.color || color || t.series[0]}
                       borderRightRadius="4px" />
                </Box>
                <Text fontSize="sm" fontWeight="semibold" color={t.text} minW="64px" textAlign="right"
                      sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {row.valueLabel}
                </Text>
              </HStack>
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

// Part-to-whole as one bar: segments {key, label, value, color}, a 2px surface gap between them,
// and a legend with each one's value and share (the legend is always there: the segments are
// told apart by more than colour).
export function ShareBar({ segments, format, height = 18 }) {
  const t = useVizTokens();
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  const shown = segments.filter((s) => (s.value || 0) > 0);
  return (
    <Box>
      <HStack spacing="2px" h={`${height}px`} bg={t.surface} borderRadius="4px" overflow="hidden">
        {shown.length === 0 && <Box flex="1" h="100%" bg={t.empty} />}
        {shown.map((s) => (
          <Tooltip key={s.key} hasArrow openDelay={100}
                   label={`${s.label}: ${format(s.value)} (${total ? Math.round((s.value / total) * 100) : 0}%)`}>
            <Box tabIndex={0} h="100%" flexGrow={s.value} flexBasis={0} minW="3px" bg={s.color}
                 _hover={{ filter: "brightness(1.08)" }}
                 _focusVisible={{ outline: "2px solid", outlineColor: t.text, outlineOffset: "-2px" }}
                 aria-label={`${s.label}: ${format(s.value)}`} />
          </Tooltip>
        ))}
      </HStack>
      <Stack spacing={1.5} mt={3}>
        {segments.map((s) => (
          <HStack key={s.key} spacing={2} fontSize="sm">
            <Box w="10px" h="10px" borderRadius="2px" bg={s.color} flexShrink={0} />
            <Text color={t.text} flex="1" noOfLines={1}>{s.label}</Text>
            <Text color={t.text} fontWeight="semibold" sx={{ fontVariantNumeric: "tabular-nums" }}>{format(s.value)}</Text>
            <Text color={t.muted} w="40px" textAlign="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {total ? `${Math.round(((s.value || 0) / total) * 100)}%` : "0%"}
            </Text>
          </HStack>
        ))}
      </Stack>
    </Box>
  );
}
